// Chart Geometry Utilities - SVG calculations for natal chart rendering

/**
 * Convert polar coordinates (angle, radius) to SVG cartesian coordinates
 * SVG coordinate system: (0,0) is top-left, Y increases downward
 * Astrological convention: 0° is on the left (Ascendant), increases counter-clockwise
 */
export const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    // Convert to radians, adjust for SVG coordinate system
    // 0° should be on the left (9 o'clock position), going counter-clockwise
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180);
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians)
    };
};

/**
 * Generate SVG arc path for a segment of the chart
 * Used for zodiac signs and house segments
 */
export const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
};

/**
 * Generate SVG path for a pie slice (wedge)
 * Used for zodiac sign backgrounds
 */
export const describeSector = (x, y, innerRadius, outerRadius, startAngle, endAngle) => {
    const startOuter = polarToCartesian(x, y, outerRadius, startAngle);
    const endOuter = polarToCartesian(x, y, outerRadius, endAngle);
    const startInner = polarToCartesian(x, y, innerRadius, startAngle);
    const endInner = polarToCartesian(x, y, innerRadius, endAngle);

    const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

    return [
        "M", startOuter.x, startOuter.y,
        "A", outerRadius, outerRadius, 0, largeArcFlag, 1, endOuter.x, endOuter.y,
        "L", endInner.x, endInner.y,
        "A", innerRadius, innerRadius, 0, largeArcFlag, 0, startInner.x, startInner.y,
        "Z"
    ].join(" ");
};

/**
 * Convert ecliptic longitude to chart angle
 * Chart starts at Ascendant (left side), Aries at 0°
 * @param longitude - Ecliptic longitude (0-360)
 * @param ascendant - Ascendant longitude (to position chart correctly)
 */
export const longitudeToChartAngle = (longitude, ascendant = 0) => {
    // Rotate so Ascendant is on the left (180° in SVG terms)
    // Counter-clockwise from Ascendant
    return (180 - longitude + ascendant + 360) % 360;
};

/**
 * Get position for planet on chart
 * Returns x, y coordinates for a given longitude
 */
export const getPlanetPosition = (longitude, ascendant, centerX, centerY, radius) => {
    const chartAngle = longitudeToChartAngle(longitude, ascendant);
    return polarToCartesian(centerX, centerY, radius, chartAngle);
};

/**
 * Get positions for house cusp labels
 */
export const getHouseLabelPositions = (houseCusps, ascendant, centerX, centerY, radius) => {
    return houseCusps.map((cusp, index) => {
        const nextCusp = houseCusps[(index + 1) % 12];
        let midAngle = (cusp + nextCusp) / 2;

        // Handle wrap-around
        if (nextCusp < cusp) {
            midAngle = ((cusp + nextCusp + 360) / 2) % 360;
        }

        const chartAngle = longitudeToChartAngle(midAngle, ascendant);
        const pos = polarToCartesian(centerX, centerY, radius, chartAngle);

        return {
            house: index + 1,
            x: pos.x,
            y: pos.y,
            angle: chartAngle
        };
    });
};

/**
 * Calculate zodiac sign segment positions for the outer wheel
 */
export const getZodiacSegments = (ascendant, centerX, centerY, innerRadius, outerRadius) => {
    const segments = [];

    for (let i = 0; i < 12; i++) {
        const signStartLon = i * 30;
        const signEndLon = (i + 1) * 30;

        const startAngle = longitudeToChartAngle(signStartLon, ascendant);
        const endAngle = longitudeToChartAngle(signEndLon, ascendant);

        // Calculate midpoint for symbol placement
        const midLon = signStartLon + 15;
        const midAngle = longitudeToChartAngle(midLon, ascendant);
        const labelRadius = (innerRadius + outerRadius) / 2;
        const labelPos = polarToCartesian(centerX, centerY, labelRadius, midAngle);

        segments.push({
            index: i,
            path: describeSector(centerX, centerY, innerRadius, outerRadius, endAngle, startAngle),
            labelX: labelPos.x,
            labelY: labelPos.y,
            startAngle,
            endAngle
        });
    }

    return segments;
};

/**
 * Calculate house division lines
 */
export const getHouseLines = (houseCusps, ascendant, centerX, centerY, innerRadius, outerRadius) => {
    return houseCusps.map((cusp, index) => {
        const chartAngle = longitudeToChartAngle(cusp, ascendant);
        const start = polarToCartesian(centerX, centerY, innerRadius, chartAngle);
        const end = polarToCartesian(centerX, centerY, outerRadius, chartAngle);

        return {
            house: index + 1,
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y,
            angle: chartAngle
        };
    });
};

/**
 * Calculate aspect lines between planets
 * Returns array of line data with styling info
 */
export const getAspectLines = (aspects, planets, ascendant, centerX, centerY, radius) => {
    return aspects.map(aspect => {
        const planet1 = planets.find(p => p.name === aspect.planet1);
        const planet2 = planets.find(p => p.name === aspect.planet2);

        if (!planet1 || !planet2) return null;

        const pos1 = getPlanetPosition(planet1.longitude, ascendant, centerX, centerY, radius);
        const pos2 = getPlanetPosition(planet2.longitude, ascendant, centerX, centerY, radius);

        return {
            ...aspect,
            x1: pos1.x,
            y1: pos1.y,
            x2: pos2.x,
            y2: pos2.y
        };
    }).filter(Boolean);
};

/**
 * Detect planet collisions and adjust positions
 * Prevents overlapping planet symbols
 */
export const adjustPlanetPositions = (planets, ascendant, centerX, centerY, radius, symbolSize = 20) => {
    const positioned = planets.map(planet => {
        const pos = getPlanetPosition(planet.longitude, ascendant, centerX, centerY, radius);
        return {
            ...planet,
            displayX: pos.x,
            displayY: pos.y,
            adjusted: false
        };
    });

    // Sort by longitude for collision detection
    positioned.sort((a, b) => a.longitude - b.longitude);

    // Check for collisions and adjust
    const minDistance = symbolSize * 1.5;

    for (let i = 0; i < positioned.length; i++) {
        for (let j = i + 1; j < positioned.length; j++) {
            const dx = positioned[j].displayX - positioned[i].displayX;
            const dy = positioned[j].displayY - positioned[i].displayY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < minDistance) {
                // Move the second planet outward slightly
                const adjustedRadius = radius + symbolSize;
                const pos = getPlanetPosition(positioned[j].longitude, ascendant, centerX, centerY, adjustedRadius);
                positioned[j].displayX = pos.x;
                positioned[j].displayY = pos.y;
                positioned[j].adjusted = true;
            }
        }
    }

    return positioned;
};

/**
 * Chart dimensions calculator
 * Returns optimal dimensions based on container size
 */
export const calculateChartDimensions = (containerWidth, containerHeight) => {
    const size = Math.min(containerWidth, containerHeight);
    const center = size / 2;
    const padding = size * 0.05;

    return {
        size,
        centerX: center,
        centerY: center,
        outerRadius: center - padding,
        zodiacInnerRadius: center * 0.75,
        planetRadius: center * 0.55,
        houseRadius: center * 0.35,
        innerRadius: center * 0.2
    };
};
